import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/** How long to wait after the pointer leaves the chat before re-anchoring to the bottom. */
const HOVER_RESUME_DELAY_MS = 20_000;
/** How close to the bottom (in px) still counts as being "at the bottom". */
const BOTTOM_THRESHOLD_PX = 48;

/**
 * Live-chat auto-scroll behavior, shared by the mobile and desktop chat panes.
 *
 * - Sticks to the bottom as new messages arrive, unless the user has scrolled up.
 * - While the chat is hovered (mouse hover or touch), auto-scrolling is paused so
 *   reading isn't interrupted.
 * - Once nothing has hovered the chat for 20 seconds, it scrolls back down to the
 *   latest message and resumes sticking.
 * - Scrolling back to the bottom manually re-enables sticking immediately.
 */
export function useChatAutoScroll(messageCount: number) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const hoveringRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  /**
   * Callback ref for the scrollable message container.
   * Anchors to the bottom whenever the container (re)mounts — e.g. on first load
   * or when the desktop chat panel is expanded again.
   */
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node) {
      stickToBottomRef.current = true;
      node.scrollTop = node.scrollHeight;
    }
  }, []);

  // Stick to the bottom as new messages arrive (unless hovered or scrolled up).
  useLayoutEffect(() => {
    if (stickToBottomRef.current && !hoveringRef.current) {
      scrollToBottom();
    }
  }, [messageCount, scrollToBottom]);

  // Clear any pending resume timer on unmount.
  useEffect(() => clearResumeTimer, [clearResumeTimer]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= BOTTOM_THRESHOLD_PX;
  }, []);

  const handlePointerEnter = useCallback(() => {
    hoveringRef.current = true;
    clearResumeTimer();
  }, [clearResumeTimer]);

  const handlePointerLeave = useCallback(() => {
    hoveringRef.current = false;
    clearResumeTimer();
    resumeTimerRef.current = window.setTimeout(() => {
      resumeTimerRef.current = null;
      stickToBottomRef.current = true;
      scrollToBottom();
    }, HOVER_RESUME_DELAY_MS);
  }, [clearResumeTimer, scrollToBottom]);

  /** Immediately re-anchor to the bottom (e.g. after the user sends a message). */
  const forceScrollToBottom = useCallback(() => {
    stickToBottomRef.current = true;
    hoveringRef.current = false;
    clearResumeTimer();
    scrollToBottom();
  }, [clearResumeTimer, scrollToBottom]);

  return {
    setContainerRef,
    forceScrollToBottom,
    containerProps: {
      onScroll: handleScroll,
      onPointerEnter: handlePointerEnter,
      onPointerLeave: handlePointerLeave,
      onPointerCancel: handlePointerLeave,
    },
  };
}
